/* eslint-disable camelcase */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@utils/cn';
import { variants } from '@components/ui/button/buttonVariants';
import { Button } from '../button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { Input } from '../form/input';
import { formatDate } from '@utils/i18n';
import { CalendarProps } from './types';
import { CalendarOptions } from './options';

function Calendar({
  className = '',
  classNames,
  showOutsideDays = true,
  onChange,
  setOpen,
  range,
  disablePast,
  notDrawer = true,
  ...props
}: CalendarProps) {
  return (
    <div className="scrollbar-thin flex max-h-[300px] flex-col gap-3 overflow-y-auto p-3 2xl:max-h-[400px] 3xl:max-h-fit">
      {props.mode === 'range' && (
        <div className="flex h-8 items-center justify-between">
          <div className="flex items-center gap-1">
            <div className="flex gap-2">
              <Input
                readOnly
                value={props.selected?.from ? formatDate(new Date(props.selected.from)) : ''}
              />
              <Input
                readOnly
                value={props.selected?.to ? formatDate(new Date(props.selected.to)) : ''}
              />
            </div>
            <Button
              variant="ghost"
              className="h-fit p-1.5 text-neutral-300 hover:text-danger-500"
              disabled={!props.selected?.from && !props.selected?.to}
              onClick={() => {
                if (!onChange) return;
                onChange({ from: undefined, to: undefined });
              }}>
              <SvgConsumer id="trash" />
            </Button>
          </div>
          <Button
            variant="ghost"
            className="h-fit p-1.5 text-neutral-300 hover:text-neutral-800"
            onClick={() => {
              if (!setOpen) return;
              setOpen(false);
            }}>
            <SvgConsumer id="close" />
          </Button>
        </div>
      )}
      <div className="flex gap-2">
        <DayPicker
          disabled={disablePast ? { before: new Date() } : undefined}
          showOutsideDays={showOutsideDays}
          fixedWeeks
          className={className}
          classNames={{
            months: cn(
              'relative flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
              props.mode === 'range' && 'border p-1.5',
            ),
            month: 'space-y-4',
            month_caption: 'flex justify-center pt-1 items-center',
            caption_label: 'text-sm font-medium',
            nav: 'absolute inset-x-1 top-0 z-10 h-9 flex justify-between items-center',
            button_previous: cn(
              variants.variant.ghost,
              'flex h-7 w-7 items-center justify-center bg-transparent p-0 opacity-50 hover:opacity-100',
            ),
            button_next: cn(
              variants.variant.ghost,
              'flex h-7 w-7 items-center justify-center bg-transparent p-0 opacity-50 hover:opacity-100',
            ),
            month_grid: 'w-full border-collapse space-y-1',
            weekdays: 'flex',
            weekday: 'text-neutral-500 rounded-md w-8 font-normal text-[0.8rem]',
            week: 'flex w-full',
            day: 'group text-center text-neutral-700 [&:has([aria-selected])]:bg-primary-200 text-sm p-0 focus-within:relative focus-within:z-20',
            day_button: 'h-8 w-8 p-0 aria-selected:opacity-100 hover:bg-primary-200',
            selected: 'bg-primary-400 text-white hover:bg-primary-600 focus:bg-primary-600',
            range_start:
              'bg-primary-400 rounded-none text-white hover:bg-primary-600 focus:bg-primary-600',
            range_end:
              'bg-primary-400 rounded-none text-white hover:bg-primary-600 focus:bg-primary-600',
            today: 'text-primary-500 font-bold',
            outside: 'text-neutral-500 opacity-50',
            disabled: 'text-neutral-500 opacity-50',
            range_middle: 'aria-selected:bg-primary-200 aria-selected:text-white',
            hidden: 'invisible',
            ...classNames,
          }}
          components={{
            Chevron: ({ orientation }) =>
              orientation === 'left' ? (
                <ChevronLeft className="pointer-events-none h-4 w-4" />
              ) : (
                <ChevronRight className="pointer-events-none h-4 w-4" />
              ),
          }}
          {...props}
        />
      </div>
      {props.mode === 'range' && notDrawer && <CalendarOptions range={range} onChange={onChange} />}
    </div>
  );
}

/* eslint-disable-next-line immutable/no-mutation */
Calendar.displayName = 'Calendar';

export { Calendar };
